import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  VStack,
  FormErrorMessage,
  useToast,
  InputGroup,
  InputLeftAddon,
  Spinner,
  Text,
} from "@chakra-ui/react";
import vendorApi from "../services/vendorApi";
import vendorPOApi from "../services/vendorPOApi";

const AddVendorPOModal = ({ isOpen, onClose, requestId, onSuccess }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [formData, setFormData] = useState({
    vendorId: "",
    poNumber: "",
    quotedAmount: "",
    currency: "INR",
    poDocumentUrl: "",
    notes: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      fetchActiveVendors();
      // Reset form when modal opens
      setFormData({
        vendorId: "",
        poNumber: "",
        quotedAmount: "",
        currency: "INR",
        poDocumentUrl: "",
        notes: "",
      });
      setErrors({});
    }
  }, [isOpen]);

  const fetchActiveVendors = async () => {
    try {
      setVendorsLoading(true);
      const response = await vendorApi.getActiveVendors();
      setVendors(response?.data || []);
    } catch (error) {
      console.error("❌ Error fetching vendors:", error);
      toast({
        title: "Error fetching vendors",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setVendorsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.vendorId) {
      newErrors.vendorId = "Please select a vendor";
    }

    if (!formData.poNumber || formData.poNumber.trim().length < 3) {
      newErrors.poNumber = "PO Number must be at least 3 characters";
    }

    if (!formData.quotedAmount || parseFloat(formData.quotedAmount) <= 0) {
      newErrors.quotedAmount = "Quoted amount must be greater than 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const proposalData = {
        ...formData,
        quotedAmount: parseFloat(formData.quotedAmount),
      };

      await vendorPOApi.addVendorPOProposal(requestId, proposalData);

      toast({
        title: "Vendor PO Proposal added successfully",
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      onSuccess();
    } catch (error) {
      console.error("❌ Error adding vendor PO proposal:", error);
      toast({
        title: "Error adding proposal",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add Vendor PO Proposal</ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit}>
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {/* Vendor Selection */}
              <FormControl isInvalid={errors.vendorId} isRequired>
                <FormLabel>Select Vendor</FormLabel>
                {vendorsLoading ? (
                  <Spinner size="sm" />
                ) : vendors.length === 0 ? (
                  <Text fontSize="sm" color="gray.500">
                    No active vendors available. Please add vendors first.
                  </Text>
                ) : (
                  <Select
                    placeholder="Select a vendor"
                    value={formData.vendorId}
                    onChange={(e) =>
                      handleInputChange("vendorId", e.target.value)
                    }
                  >
                    {vendors.map((vendor) => (
                      <option key={vendor._id} value={vendor._id}>
                        {vendor.name} ({vendor.email})
                      </option>
                    ))}
                  </Select>
                )}
                <FormErrorMessage>{errors.vendorId}</FormErrorMessage>
              </FormControl>

              {/* PO Number */}
              <FormControl isInvalid={errors.poNumber} isRequired>
                <FormLabel>PO Number</FormLabel>
                <Input
                  placeholder="e.g., PO-2026-001"
                  value={formData.poNumber}
                  onChange={(e) =>
                    handleInputChange("poNumber", e.target.value)
                  }
                />
                <FormErrorMessage>{errors.poNumber}</FormErrorMessage>
              </FormControl>

              {/* Quoted Amount & Currency */}
              <FormControl isInvalid={errors.quotedAmount} isRequired>
                <FormLabel>Quoted Amount</FormLabel>
                <InputGroup>
                  <InputLeftAddon>
                    <Select
                      value={formData.currency}
                      onChange={(e) =>
                        handleInputChange("currency", e.target.value)
                      }
                      variant="unstyled"
                      width="70px"
                    >
                      <option value="INR">₹ INR</option>
                      <option value="USD">$ USD</option>
                      <option value="EUR">€ EUR</option>
                      <option value="GBP">£ GBP</option>
                    </Select>
                  </InputLeftAddon>
                  <Input
                    type="number"
                    placeholder="50000"
                    value={formData.quotedAmount}
                    onChange={(e) =>
                      handleInputChange("quotedAmount", e.target.value)
                    }
                  />
                </InputGroup>
                <FormErrorMessage>{errors.quotedAmount}</FormErrorMessage>
              </FormControl>

              {/* PO Document URL */}
              <FormControl>
                <FormLabel>PO Document URL (Optional)</FormLabel>
                <Input
                  placeholder="https://example.com/documents/po.pdf"
                  value={formData.poDocumentUrl}
                  onChange={(e) =>
                    handleInputChange("poDocumentUrl", e.target.value)
                  }
                />
              </FormControl>

              {/* Notes */}
              <FormControl>
                <FormLabel>Notes (Optional)</FormLabel>
                <Textarea
                  placeholder="Payment terms, delivery timeline, special conditions..."
                  value={formData.notes}
                  onChange={(e) => handleInputChange("notes", e.target.value)}
                  rows={3}
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              type="submit"
              isLoading={loading}
              isDisabled={vendors.length === 0}
            >
              Add Proposal
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default AddVendorPOModal;
